// Token storage: web → localStorage, native → @capacitor/preferences.
// Никакого refresh-token mechanism — backend sliding-TTL продлевает токен сам
// (Sprint 1 D-11). Frontend держит токен до первого 401.
// Source of truth: docs/specs/sprint-3/stage-1-contracts.md §7.2.
//
// Stage 4 addition (v0.93.1 review fix): in-memory snapshot + listener API.
// - Web: snapshot НЕ кэшируется, sync read из localStorage на каждом вызове
//   getToken/peekToken. Это (а) полностью устраняет async-race на web (await
//   не происходит), (б) сохраняет работающий V-9 (`localStorage.clear()` в
//   DevTools → следующий axios-запрос видит null → 401 → /signin redirect).
// - Native: snapshot кэшируется (Preferences.get async, дорогой), но getToken
//   защищён от race: in-flight load checks `snapshot === undefined` ПОСЛЕ await,
//   чтобы set/clearToken не был перезатёрт устаревшим persisted-значением.

import { Preferences } from '@capacitor/preferences';
import { isNative } from './platform';

const TOKEN_KEY = 'healthlab.auth.token';

let snapshot: string | null | undefined = undefined; // undefined = native cold-start
let loadPromise: Promise<string | null> | null = null;
const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach((cb) => cb());
}

function readWebSync(): string | null {
  const v = window.localStorage.getItem(TOKEN_KEY);
  // Side-sync кэша для useSyncExternalStore: getSnapshot всегда видит свежее.
  // Не notify — внешние изменения (DevTools) не должны триггерить notify-каскад;
  // настоящий триггер на refresh — 401-interceptor дёрнет clearToken().
  snapshot = v;
  return v;
}

async function loadNativeFromPreferences(): Promise<string | null> {
  const { value } = await Preferences.get({ key: TOKEN_KEY });
  return value ?? null;
}

export async function getToken(): Promise<string | null> {
  if (!isNative()) {
    return readWebSync();
  }
  if (snapshot !== undefined) return snapshot;
  if (!loadPromise) {
    loadPromise = (async () => {
      try {
        const persisted = await loadNativeFromPreferences();
        // RACE FIX: если за время await'а setToken/clearToken уже записал свежее
        // значение в snapshot, не перезатираем устаревшим persisted.
        if (snapshot === undefined) {
          snapshot = persisted;
          notify();
        }
        return snapshot ?? null;
      } finally {
        // Reset даже при reject — иначе одна ошибка Preferences.get() заблокировала
        // бы все последующие getToken() до перезагрузки приложения.
        loadPromise = null;
      }
    })();
  }
  await loadPromise;
  return snapshot ?? null;
}

export async function setToken(token: string): Promise<void> {
  if (isNative()) {
    await Preferences.set({ key: TOKEN_KEY, value: token });
  } else {
    window.localStorage.setItem(TOKEN_KEY, token);
  }
  snapshot = token;
  notify();
}

export async function clearToken(): Promise<void> {
  if (isNative()) {
    await Preferences.remove({ key: TOKEN_KEY });
  } else {
    window.localStorage.removeItem(TOKEN_KEY);
  }
  snapshot = null;
  notify();
}

// Sync-snapshot для useSyncExternalStore. На web всегда возвращает свежее
// значение из localStorage; на native — кэш (undefined до первого load'а).
export function peekToken(): string | null | undefined {
  if (!isNative()) {
    return readWebSync();
  }
  return snapshot;
}

export function subscribeToken(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}
